<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button class="btn btn-secondary" @click="$router.push('/client/members/import-history')">
          <i class="ri-history-line"></i>
          匯入/匯出紀錄
        </button>
        <button class="btn btn-secondary" :disabled="exporting" @click="handleExport">
          <i class="ri-download-2-line"></i>
          {{ exporting ? '建立匯出中...' : '匯出' }}
        </button>
        <button
          class="btn btn-secondary"
          :title="isWixtarClient ? '導入 CRM 鏡像（不會寫回 Wixtar）；大檔於背景分段處理' : ''"
          @click="openImportModal"
        >
          <i class="ri-upload-2-line"></i>
          批次匯入
        </button>
        <button
          class="btn btn-primary"
          :title="isWixtarClient ? '將先在 Wixtar 建立，再同步至本系統' : ''"
          @click="handleCreate"
        >
          <i class="ri-add-line"></i>
          新增會員
        </button>
      </div>
    </div>

    <div class="page-container">

    <div v-if="isWixtarClient" class="alert alert-info wixtar-banner" role="status">
      <strong>Wixtar 客戶：</strong>會員主檔以 Wixtar 為準，本頁列表為 CRM 鏡像。
      新增會員會先寫入 Wixtar 再同步至此；批次匯入僅寫 CRM 鏡像（不回寫 Wixtar）。
      編輯與重設密碼請至 Wixtar；點數餘額為同步副本，即時值請於
      <router-link to="/client/points/transactions">點數交易記錄</router-link>查詢。
    </div>

    <!-- 匯入進度條 Banner（放在 page-container 內，自然落在 fixed header 的 padding 區之後） -->
    <div v-if="activeImportJob" class="import-progress-banner" :class="`banner-${activeImportJob.status}`">
      <div class="banner-icon">
        <i v-if="['pending','processing'].includes(activeImportJob.status)" class="ri-loader-4-line spinning"></i>
        <i v-else-if="activeImportJob.status === 'completed'" class="ri-checkbox-circle-line"></i>
        <i v-else-if="activeImportJob.status === 'partial'" class="ri-alert-line"></i>
        <i v-else class="ri-error-warning-line"></i>
      </div>
      <div class="banner-content">
        <div class="banner-text">{{ importJobStatusText }}</div>
        <div v-if="['pending','processing'].includes(activeImportJob.status)" class="banner-bar-wrap">
          <div class="banner-bar" :style="{ width: importJobProgress + '%' }"></div>
        </div>
        <div v-if="activeImportJob.jobNumber" class="banner-sub">批次號：{{ activeImportJob.jobNumber }}</div>
      </div>
      <div class="banner-actions">
        <button class="btn btn-sm btn-link" @click="$router.push('/client/members/import-history')">查看紀錄</button>
        <button class="btn btn-sm btn-icon" @click="clearActiveJob" title="關閉"><i class="ri-close-line"></i></button>
      </div>
    </div>

    <!-- 匯出進度條 Banner（顯示進行中或最近一次匯出） -->
    <div v-if="activeExportJob" class="import-progress-banner" :class="`banner-${activeExportJob.status}`">
      <div class="banner-icon">
        <i v-if="['pending','processing'].includes(activeExportJob.status)" class="ri-loader-4-line spinning"></i>
        <i v-else-if="activeExportJob.status === 'completed'" class="ri-checkbox-circle-line"></i>
        <i v-else class="ri-error-warning-line"></i>
      </div>
      <div class="banner-content">
        <div class="banner-text">{{ exportJobStatusText }}</div>
        <div v-if="['pending','processing'].includes(activeExportJob.status)" class="banner-bar-wrap">
          <div class="banner-bar" :style="{ width: exportJobProgress + '%' }"></div>
        </div>
        <div class="banner-sub">
          匯出編號：{{ activeExportJob.jobNumber }}
          <template v-if="exportJobHasFilters">・套用篩選條件</template>
          <template v-else>・全量匯出</template>
          <template v-if="activeExportJob.status === 'completed' && activeExportJob.fileExpiresAt">
            ・檔案保留至 {{ formatDate(activeExportJob.fileExpiresAt) }}
          </template>
          <template v-if="activeExportJob.status === 'failed' && activeExportJob.errorSummary">
            ・{{ activeExportJob.errorSummary }}
          </template>
        </div>
      </div>
      <div class="banner-actions">
        <button
          v-if="activeExportJob.status === 'completed' && !exportFileExpired"
          class="btn btn-sm btn-primary"
          :disabled="downloadingExport"
          @click="handleExportDownload"
        >
          <i class="ri-download-2-line"></i>
          {{ downloadingExport ? '下載中...' : '下載 CSV' }}
        </button>
        <span v-else-if="activeExportJob.status === 'completed' && exportFileExpired" class="banner-expired">
          檔案已過期
        </span>
        <button class="btn btn-sm btn-link" @click="$router.push('/client/members/import-history?tab=export')">查看紀錄</button>
        <button class="btn btn-sm btn-icon" @click="dismissExportBanner" title="關閉"><i class="ri-close-line"></i></button>
      </div>
    </div>

    <div class="page-toolbar">
      <div class="search-box">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="搜尋會員編號、姓名、電話、Email..."
          @input="handleSearch"
        />
        <i class="ri-search-line"></i>
      </div>
      <div class="filter-group">
        <select v-model="filterStatus" class="form-control" @change="handleFilterChange">
          <option value="">全部狀態</option>
          <option value="active">啟用</option>
          <option value="inactive">停用</option>
          <option value="pending">待審核</option>
          <option value="blocked">已封鎖</option>
        </select>
      </div>
      <button class="btn btn-secondary advanced-toggle" @click="toggleAdvancedSearch">
        <i class="ri-filter-3-line"></i>
        進階搜尋
        <span v-if="activeAdvancedFilterCount > 0" class="filter-count">
          {{ activeAdvancedFilterCount }}
        </span>
      </button>
    </div>

    <div v-if="showAdvancedSearch" class="advanced-search-panel">
      <div class="advanced-section">
        <h3 class="advanced-section-title">標籤</h3>
        <TagSelectorMultiple
          v-model="advancedFilters.tagIds"
          hint="可依多個會員標籤搜尋"
          :show-selected="true"
        />
        <div class="advanced-grid compact-grid">
          <div class="form-group">
            <label>標籤符合方式</label>
            <select v-model="advancedFilters.tagMatchType" class="form-control">
              <option value="OR">符合任一標籤</option>
              <option value="AND">同時符合全部標籤</option>
            </select>
          </div>
        </div>
      </div>

      <div class="advanced-section">
        <h3 class="advanced-section-title">加入會員時間</h3>
        <div class="advanced-grid">
          <div class="form-group">
            <label>起</label>
            <input v-model="advancedFilters.registeredStart" type="date" class="form-control" />
          </div>
          <div class="form-group">
            <label>迄</label>
            <input v-model="advancedFilters.registeredEnd" type="date" class="form-control" />
          </div>
        </div>
      </div>

      <div class="advanced-section">
        <h3 class="advanced-section-title">生日</h3>
        <div class="advanced-grid">
          <div class="form-group">
            <label>生日起</label>
            <input v-model="advancedFilters.birthdayStart" type="date" class="form-control" />
          </div>
          <div class="form-group">
            <label>生日迄</label>
            <input v-model="advancedFilters.birthdayEnd" type="date" class="form-control" />
          </div>
          <div class="form-group">
            <label>生日月份</label>
            <select v-model="advancedFilters.birthdayMonth" class="form-control">
              <option value="">全部月份</option>
              <option v-for="month in monthOptions" :key="month" :value="month">
                {{ month }} 月
              </option>
            </select>
          </div>
          <div class="form-group">
            <label>生日日期</label>
            <select v-model="advancedFilters.birthdayDay" class="form-control">
              <option value="">全部日期</option>
              <option v-for="day in dayOptions" :key="day" :value="day">
                {{ day }} 日
              </option>
            </select>
          </div>
        </div>
      </div>

      <div class="advanced-section">
        <h3 class="advanced-section-title">地址</h3>
        <div class="advanced-grid">
          <div class="form-group">
            <label>縣市</label>
            <select
              v-model="advancedFilters.addressCity"
              class="form-control"
              :disabled="loadingCities"
              @change="handleCityChange"
            >
              <option value="">全部縣市</option>
              <option
                v-for="city in cities"
                :key="city.id"
                :value="city.cityName"
                :data-city-id="city.id"
              >
                {{ city.cityName }}
              </option>
            </select>
          </div>
          <div class="form-group">
            <label>鄉鎮區</label>
            <select
              v-model="advancedFilters.addressDistrict"
              class="form-control"
              :disabled="!selectedCityId || loadingDistricts || districts.length === 0"
            >
              <option value="">全部鄉鎮區</option>
              <option
                v-for="district in districts"
                :key="district.id"
                :value="district.districtName"
              >
                {{ district.districtName }}
              </option>
            </select>
          </div>
          <div class="form-group full-width">
            <label>詳細地址</label>
            <input
              v-model.trim="advancedFilters.addressStreet"
              type="text"
              class="form-control"
              placeholder="輸入路名、街道或門牌"
            />
          </div>
        </div>
      </div>

      <div class="advanced-actions">
        <button class="btn btn-primary" @click="applyAdvancedFilters">
          <i class="ri-search-line"></i>
          套用搜尋
        </button>
        <button class="btn btn-secondary" @click="resetAdvancedFilters">
          清除進階條件
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchMembers">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>會員編號</th>
            <th>姓名</th>
            <th>手機號碼</th>
            <th>Email</th>
            <th>會員等級</th>
            <th>狀態</th>
            <th>累積消費</th>
            <th>
              點數餘額
              <span v-if="isWixtarClient" class="th-hint" title="Wixtar 同步後的鏡像值，即時餘額以 Wixtar 為準">（Wixtar 同步）</span>
            </th>
            <th>註冊時間</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="members.length === 0">
            <td colspan="10" class="empty-state">
              沒有找到會員
            </td>
          </tr>
          <tr v-for="member in members" :key="member.id">
            <td>{{ member.memberCode || '-' }}</td>
            <td>{{ member.name || '-' }}</td>
            <td>{{ member.phone || '-' }}</td>
            <td>{{ member.email || '-' }}</td>
            <td>
              <span v-if="member.tierName" class="badge badge-info">
                {{ member.tierName }}
              </span>
              <span v-else class="badge badge-secondary">-</span>
            </td>
            <td>
              <span
                :class="getStatusBadgeClass(member.status)"
              >
                {{ getStatusText(member.status) }}
              </span>
            </td>
            <td>{{ formatCurrency(member.totalSpent) }}</td>
            <td>{{ formatNumber(member.availablePoints) }}</td>
            <td>{{ formatDate(member.registeredAt || member.createdAt) }}</td>
            <td>
              <div class="action-buttons">
                <button
                  class="btn btn-sm btn-secondary"
                  @click="handleView(member)"
                  title="查看詳情"
                >
                  <i class="ri-eye-line"></i>
                </button>
                <button
                  class="btn btn-sm btn-secondary"
                  :disabled="isWixtarClient"
                  @click="handleEdit(member)"
                  :title="isWixtarClient ? 'Wixtar 會員系統：請至 Wixtar 編輯會員資料' : '編輯'"
                >
                  <i class="ri-edit-line"></i>
                </button>
                <button
                  class="btn btn-sm btn-danger"
                  @click="handleDelete(member)"
                  title="刪除"
                >
                  <i class="ri-delete-bin-line"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ListPagination
      v-if="total > 0"
      :page="currentPage"
      :total-pages="totalPages"
      :total="total"
      @change="goToPage"
    />
    </div>

    <!-- 批次匯入 Modal -->
    <div v-if="showImportModal" class="modal-overlay" @click.self="closeImportModal">
      <div class="modal-container">
        <div class="modal-header">
          <h3>批次匯入會員</h3>
          <button type="button" class="modal-close" @click="closeImportModal">
            <i class="ri-close-line"></i>
          </button>
        </div>
        <div class="modal-body">
          <p v-if="isWixtarClient" class="import-hint import-hint-wixtar">
            Wixtar 模式：上傳 Wixtar 匯出 CSV 後<strong>手動對應欄位</strong>寫入 CRM 鏡像。
            <strong>不會回寫 Wixtar</strong>。大檔（數十萬筆以上）會直傳 S3，由後端背景分段處理。
          </p>
          <p v-else class="import-hint">
            上傳 CSV 檔案，第一列為欄位名稱。必填：<code>name</code>（姓名）、<code>phone</code>（手機）；
            選填：email、birthday、gender、member_tier、status、address_city、address_district、address_street、address_postal_code。
            大檔會直傳 S3，由後端背景分段處理（最多 1,000,000 筆）。
          </p>
          <div v-if="!isWixtarClient" class="import-template-row">
            <span class="import-template-label">CSV 範本</span>
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="downloadImportTemplate">
              <i class="ri-download-line"></i>
              下載範本 CSV
            </button>
          </div>
          <div v-if="!isWixtarClient" class="import-sample">
            <strong>範例預覽：</strong>
            <pre>name,phone,email,birthday,gender,member_tier,status
王小明,0912345678,user@example.com,1990-01-01,male,regular,active</pre>
          </div>
          <div class="form-group import-strategy">
            <label>手機號碼已存在時</label>
            <select v-model="importDuplicateStrategy" class="form-control">
              <option value="merge">合併（將指定欄位更新到既有會員）</option>
              <option value="skip">略過（保留既有會員，不做任何變更）</option>
            </select>
          </div>

          <!-- 合併欄位勾選（僅在選「合併」且已上傳檔案時顯示） -->
          <div v-if="importDuplicateStrategy === 'merge' && availableMergeFields.length > 0" class="merge-fields-section">
            <div class="merge-fields-header">
              <span class="merge-fields-title">
                <i class="ri-git-merge-line"></i>
                合併時要更新的欄位
              </span>
              <div class="merge-fields-bulk">
                <button type="button" class="btn-text" @click="selectAllMergeFields">全選</button>
                <span class="divider">|</span>
                <button type="button" class="btn-text" @click="clearAllMergeFields">全不選</button>
              </div>
            </div>
            <div class="merge-fields-grid">
              <label
                v-for="field in availableMergeFields"
                :key="field.key"
                class="merge-field-item"
                :class="{ 'is-checked': selectedMergeFields.includes(field.key) }"
              >
                <input
                  type="checkbox"
                  :value="field.key"
                  v-model="selectedMergeFields"
                />
                <span class="field-label">{{ field.label }}</span>
                <span class="field-example" v-if="field.example">{{ field.example }}</span>
              </label>
            </div>
            <p class="merge-fields-hint">
              <i class="ri-information-line"></i>
              勾選的欄位：若 CSV 中有值，會覆蓋既有會員資料。未勾選的欄位：保留既有資料，不做變更。
            </p>
          </div>

          <div
            class="import-dropzone"
            :class="{ 'is-dragover': importDragging }"
            @dragover.prevent="importDragging = true"
            @dragleave.prevent="importDragging = false"
            @drop.prevent="handleImportDrop"
          >
            <input
              ref="importFileInput"
              type="file"
              accept=".csv,text/csv,text/plain"
              style="display: none"
              @change="handleImportFileSelect"
            />
            <template v-if="importFileName">
              <i class="ri-file-text-line"></i>
              <span>
                {{ importFileName }}
                <template v-if="importSampleOnly">（大型檔案，背景處理）</template>
                <template v-else>（{{ importItems.length }} 筆）</template>
              </span>
              <button type="button" class="btn btn-sm btn-secondary" @click="clearImportFile">重新選擇</button>
            </template>
            <template v-else>
              <i class="ri-upload-cloud-2-line"></i>
              <span>拖曳 CSV 到此，或</span>
              <button type="button" class="btn btn-sm btn-primary" @click="triggerImportFileSelect">
                選擇檔案
              </button>
            </template>
          </div>
          <p v-if="importParseError" class="import-error">{{ importParseError }}</p>
          <p v-if="importFile && importSampleOnly" class="import-large-file-hint">
            <i class="ri-information-line"></i>
            已偵測為大型檔案，僅取樣表頭與前幾列供對應／預覽；完整筆數由後端背景掃描後顯示於進度條。
            <template v-if="importSampleRowCount">取樣約 {{ importSampleRowCount }} 列。</template>
          </p>

          <!-- Wixtar：手動欄位對應 -->
          <div v-if="isWixtarClient && importCsvHeaders.length > 0" class="column-mapping-section">
            <div class="column-mapping-header">
              <i class="ri-guide-line"></i>
              <span>欄位對應</span>
              <span class="column-mapping-hint">請將 CSV 欄位對到 CRM 欄位；手機為必填</span>
            </div>
            <div class="column-mapping-rows">
              <div
                v-for="field in importMappingFields"
                :key="field.key"
                class="column-mapping-row"
              >
                <label class="column-mapping-label">
                  {{ field.label }}
                  <span v-if="field.required" class="required-mark">*</span>
                </label>
                <select v-model="columnMapping[field.key]" class="form-control">
                  <option value="">（不匯入）</option>
                  <option v-for="h in importCsvHeaders" :key="h" :value="h">{{ h }}</option>
                </select>
              </div>
            </div>
          </div>

          <!-- 性別欄位值對應 -->
          <div v-if="detectedGenderValues.length > 0" class="gender-mapping-section">
            <div class="gender-mapping-header">
              <i class="ri-men-line"></i>
              <span>性別欄位對應</span>
              <span class="gender-mapping-hint">請確認 CSV 中的性別值對應到正確的選項</span>
            </div>
            <div class="gender-mapping-rows">
              <div
                v-for="val in detectedGenderValues"
                :key="val"
                class="gender-mapping-row"
              >
                <span class="gender-raw-value">{{ val || '（空白）' }}</span>
                <i class="ri-arrow-right-line gender-arrow"></i>
                <select v-model="genderMapping[val]" class="form-control gender-select">
                  <option value="">未設定</option>
                  <option value="male">男（male）</option>
                  <option value="female">女（female）</option>
                  <option value="neutral">中性（neutral）</option>
                </select>
                <span
                  class="gender-auto-badge"
                  :class="genderMapping[val] ? 'is-mapped' : 'is-unknown'"
                >
                  {{ genderMapping[val] ? '自動辨識' : '需手動設定' }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeImportModal">取消</button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="importing || !canSubmitImport"
            @click="handleImportSubmit"
          >
            {{ importSubmitLabel }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { memberService } from '@/api/modules/members.js';
import { cityService } from '@/api/modules/cities.js';
import { clientService } from '@/api/modules/clients.js';
import authService from '@/helpers/authservice/authService.js';
import { showSuccess, showError, showDeleteConfirm } from '@/utils/swal.js';
import TagSelectorMultiple from '@/components/TagSelectorMultiple.vue';
import Swal from 'sweetalert2';

export default {
  name: 'ClientMembers',
  components: {
    TagSelectorMultiple
  },
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('會員管理');
    const members = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const searchQuery = ref('');
    const filterStatus = ref('');
    const isWixtarClient = ref(false);
    const showAdvancedSearch = ref(false);
    const advancedFilters = ref({
      tagIds: [],
      tagMatchType: 'OR',
      registeredStart: '',
      registeredEnd: '',
      birthdayStart: '',
      birthdayEnd: '',
      birthdayMonth: '',
      birthdayDay: '',
      addressCity: '',
      addressDistrict: '',
      addressStreet: ''
    });
    const currentPage = ref(1);
    const pageSize = ref(20);
    const total = ref(0);
    const searchTimeout = ref(null);
    const monthOptions = Array.from({ length: 12 }, (_, index) => index + 1);
    const dayOptions = Array.from({ length: 31 }, (_, index) => index + 1);
    const cities = ref([]);
    const districts = ref([]);
    const selectedCityId = ref('');
    const loadingCities = ref(false);
    const loadingDistricts = ref(false);
    const showImportModal = ref(false);
    const importDragging = ref(false);
    const importFileName = ref('');
    const importFile = ref(null);      // 原始 CSV File 物件（直傳 S3 用）
    const importItems = ref([]);
    const importParseError = ref(null);
    const importing = ref(false);
    const importDuplicateStrategy = ref('merge');
    const importFileInput = ref(null);
    const importCsvHeaders = ref([]);
    const importSampleOnly = ref(false);
    const importSampleRowCount = ref(0);
    const columnMapping = ref({});
    // 大檔只取樣前 N bytes，避免瀏覽器整檔解析卡住；完整筆數由後端 S3 串流掃描
    const IMPORT_SAMPLE_BYTES = 512 * 1024;

    const importMappingFields = [
      { key: 'phone', label: '手機', required: true },
      { key: 'name', label: '姓名', required: false },
      { key: 'wixtar_member_id', label: 'Wixtar 會員 ID', required: false },
      { key: 'email', label: 'Email', required: false },
      { key: 'birthday', label: '生日', required: false },
      { key: 'gender', label: '性別', required: false },
      { key: 'member_tier', label: '會員等級', required: false },
      { key: 'status', label: '狀態', required: false },
      { key: 'address_city', label: '縣市', required: false },
      { key: 'address_district', label: '鄉鎮區', required: false },
      { key: 'address_street', label: '地址', required: false },
      { key: 'address_postal_code', label: '郵遞區號', required: false },
      { key: 'available_points', label: '點數餘額（鏡像）', required: false }
    ];

    // 欄位中文對照表（key = normalizeMemberRow 產出的欄位名）
    const FIELD_LABELS = {
      name:             { label: '姓名' },
      gender:           { label: '性別' },
      birthday:         { label: '生日' },
      email:            { label: 'Email' },
      notes:            { label: '備註' },
      status:           { label: '狀態' },
      city:             { label: '縣市' },
      district:         { label: '鄉鎮市區' },
      address:          { label: '地址' },
      referral_code:    { label: '推薦碼' },
      referral_source:  { label: '推薦來源' },
      member_tier:      { label: '會員等級' },
      address_city:     { label: '縣市' },
      address_district: { label: '鄉鎮區' },
      address_street:   { label: '地址' },
      address_postal_code: { label: '郵遞區號' },
      wixtar_member_id: { label: 'Wixtar 會員 ID' },
      available_points: { label: '點數餘額' }
    };
    // 不可合併的系統欄位（不讓使用者勾選）
    const NON_MERGEABLE_FIELDS = new Set(['phone', 'registration_method', 'registration_source', 'data_source', 'points']);

    const canSubmitImport = computed(() => {
      if (!importFile.value || importing.value) return false;
      if (isWixtarClient.value) {
        return Boolean(columnMapping.value.phone);
      }
      return importItems.value.length > 0 || importSampleOnly.value;
    });

    const importSubmitLabel = computed(() => {
      if (importing.value) return '匯入中...';
      if (importSampleOnly.value) return '開始背景匯入';
      const n = importItems.value.length || 0;
      return `匯入 ${n} 筆`;
    });

    // 根據 CSV 解析結果動態產生可勾選的合併欄位
    const availableMergeFields = ref([]);   // [{ key, label, example }]
    const selectedMergeFields = ref([]);    // 預設全選

    function selectAllMergeFields() {
      selectedMergeFields.value = availableMergeFields.value.map(f => f.key);
    }
    function clearAllMergeFields() {
      selectedMergeFields.value = [];
    }

    // 性別欄位對應：{ '原始值' -> 'male'|'female'|'neutral'|'' }
    const genderMapping = ref({});
    // CSV 中偵測到的所有唯一 gender 值（未經轉換）
    const detectedGenderValues = ref([]);

    // 內建能自動辨識的 gender 別名
    const BUILTIN_GENDER_MAP = {
      male: 'male', m: 'male', '男': 'male', '男性': 'male', '男生': 'male',
      female: 'female', f: 'female', '女': 'female', '女性': 'female', '女生': 'female',
      neutral: 'neutral', '中性': 'neutral', other: 'neutral', '其他': 'neutral', '無': 'neutral'
    };

    const totalPages = computed(() => {
      return Math.ceil(total.value / pageSize.value);
    });

    const activeAdvancedFilterCount = computed(() => {
      const filters = advancedFilters.value;
      let count = 0;
      if (filters.tagIds.length > 0) count++;
      if (filters.registeredStart || filters.registeredEnd) count++;
      if (filters.birthdayStart || filters.birthdayEnd) count++;
      if (filters.birthdayMonth) count++;
      if (filters.birthdayDay) count++;
      if (filters.addressCity || filters.addressDistrict || filters.addressStreet) count++;
      return count;
    });

    const loadClientMemberSystem = async () => {
      const user = authService.getCurrentUser();
      if (!user?.clientId) return;
      try {
        const res = await clientService.getClient(user.clientId);
        isWixtarClient.value = res?.data?.clientData?.memberSystemType === 'WIXTAR';
      } catch (err) {
        console.error('載入客戶會員系統類型失敗:', err);
        isWixtarClient.value = false;
      }
    };

    const fetchMembers = async () => {
      loading.value = true;
      error.value = null;

      try {
        const params = {
          page: currentPage.value,
          pageSize: pageSize.value,
        };

        if (searchQuery.value) {
          params.search = searchQuery.value;
        }

        if (filterStatus.value) {
          params.status = filterStatus.value;
        }

        const filters = advancedFilters.value;
        if (filters.tagIds.length > 0) {
          params.tagIds = filters.tagIds.join(',');
          params.tagMatchType = filters.tagMatchType || 'OR';
        }
        if (filters.registeredStart) params.registeredStart = filters.registeredStart;
        if (filters.registeredEnd) params.registeredEnd = filters.registeredEnd;
        if (filters.birthdayStart) params.birthdayStart = filters.birthdayStart;
        if (filters.birthdayEnd) params.birthdayEnd = filters.birthdayEnd;
        if (filters.birthdayMonth) params.birthdayMonth = Number(filters.birthdayMonth);
        if (filters.birthdayDay) params.birthdayDay = Number(filters.birthdayDay);
        if (filters.addressCity) params.addressCity = filters.addressCity;
        if (filters.addressDistrict) params.addressDistrict = filters.addressDistrict;
        if (filters.addressStreet) params.addressStreet = filters.addressStreet;

        const response = await memberService.getMembers(params);

        console.log('[ClientMembers] API Response:', response);

        if (response && response.success && response.data) {
          members.value = response.data.items || [];
          total.value = response.data.pagination?.totalItems || 0;
        } else {
          const errorMsg = (response && response.message) || (response && response.error && response.error.message) || '獲取會員列表失敗';
          console.error('[ClientMembers] API Error:', {
            response,
            errorMsg,
            success: response?.success,
            data: response?.data,
            message: response?.message,
            error: response?.error
          });
          error.value = errorMsg;
        }
      } catch (err) {
        console.error('[ClientMembers] Exception:', {
          error: err,
          message: err.message,
          response: err.response,
          data: err.data,
          code: err.code,
          details: err.details
        });
        error.value = err.message || err.data?.message || '獲取會員列表失敗';
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
      searchTimeout.value = setTimeout(() => {
        currentPage.value = 1;
        fetchMembers();
      }, 500);
    };

    const handleFilterChange = () => {
      currentPage.value = 1;
      fetchMembers();
    };

    const toggleAdvancedSearch = () => {
      showAdvancedSearch.value = !showAdvancedSearch.value;
    };

    const applyAdvancedFilters = () => {
      currentPage.value = 1;
      fetchMembers();
    };

    const resetAdvancedFilters = () => {
      advancedFilters.value = {
        tagIds: [],
        tagMatchType: 'OR',
        registeredStart: '',
        registeredEnd: '',
        birthdayStart: '',
        birthdayEnd: '',
        birthdayMonth: '',
        birthdayDay: '',
        addressCity: '',
        addressDistrict: '',
        addressStreet: ''
      };
      selectedCityId.value = '';
      districts.value = [];
      currentPage.value = 1;
      fetchMembers();
    };

    const loadCities = async () => {
      loadingCities.value = true;
      try {
        const response = await cityService.getCities();
        if (response && response.success && response.data) {
          cities.value = response.data;
        }
      } catch (err) {
        console.error('載入城市列表失敗:', err);
      } finally {
        loadingCities.value = false;
      }
    };

    const loadDistricts = async (cityId) => {
      if (!cityId) {
        districts.value = [];
        return;
      }

      loadingDistricts.value = true;
      try {
        const response = await cityService.getDistrictsByCityId(cityId);
        if (response && response.success && response.data) {
          districts.value = response.data;
        }
      } catch (err) {
        console.error('載入區域列表失敗:', err);
        districts.value = [];
      } finally {
        loadingDistricts.value = false;
      }
    };

    const handleCityChange = (event) => {
      const selectedOption = event.target.options[event.target.selectedIndex];
      const cityId = selectedOption.getAttribute('data-city-id');

      selectedCityId.value = cityId || '';
      advancedFilters.value.addressCity = selectedOption.value;
      advancedFilters.value.addressDistrict = '';
      districts.value = [];

      if (cityId) {
        loadDistricts(cityId);
      }
    };

    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
        fetchMembers();
      }
    };

    const handleCreate = () => {
      router.push('/client/members/create');
    };

    const resetImportState = () => {
      importFileName.value = '';
      importFile.value = null;
      importItems.value = [];
      importParseError.value = null;
      genderMapping.value = {};
      detectedGenderValues.value = [];
      availableMergeFields.value = [];
      selectedMergeFields.value = [];
      importCsvHeaders.value = [];
      importSampleOnly.value = false;
      importSampleRowCount.value = 0;
      columnMapping.value = {};
    };

    const openImportModal = () => {
      showImportModal.value = true;
      importDuplicateStrategy.value = 'merge';
      resetImportState();
    };

    const closeImportModal = () => {
      if (importing.value) return;
      showImportModal.value = false;
      importDragging.value = false;
      resetImportState();
    };

    const downloadImportTemplate = () => {
      const sample = [
        'name,phone,email,birthday,gender,member_tier,status,address_city,address_district,address_street,address_postal_code',
        '王小明,0912345678,user@example.com,1990-01-01,male,regular,active,台北市,大安區,信義路四段1號,106',
        '李小華,0922333444,,,,regular,active,,,,'
      ].join('\n');
      const blob = new Blob(['\uFEFF' + sample], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = '會員匯入範本.csv';
      a.click();
      URL.revokeObjectURL(url);
    };

    const triggerImportFileSelect = () => {
      importFileInput.value?.click();
    };

    const clearImportFile = () => {
      resetImportState();
      if (importFileInput.value) {
        importFileInput.value.value = '';
      }
    };

    const FIELD_ALIASES_FOR_PREFILL = {
      name: ['name', '姓名'],
      phone: ['phone', '手機', '手機號碼', 'mobile', '電話'],
      email: ['email', '信箱'],
      birthday: ['birthday', '生日', '出生日期'],
      gender: ['gender', '性別'],
      member_tier: ['member_tier', 'membertier', '會員等級', '等級'],
      status: ['status', '狀態'],
      address_city: ['address_city', 'addresscity', '縣市', '城市'],
      address_district: ['address_district', 'addressdistrict', '區域', '鄉鎮區', '區'],
      address_street: ['address_street', 'addressstreet', '地址', '詳細地址', '街道'],
      address_postal_code: ['address_postal_code', 'addresspostalcode', '郵遞區號', '郵遞區碼'],
      wixtar_member_id: ['wixtar_member_id', 'wixtarmemberid', '會員id', '會員編號', 'member_id', 'memberid'],
      available_points: ['available_points', 'availablepoints', '點數', '點數餘額', 'points', 'point']
    };

    const prefillColumnMapping = (rawHeaders) => {
      const normalized = rawHeaders.map((h) => ({ raw: h, norm: normalizeHeader(h) }));
      const mapping = {};
      for (const field of importMappingFields) {
        const aliases = FIELD_ALIASES_FOR_PREFILL[field.key] || [field.key];
        const hit = normalized.find((h) => aliases.some((a) => h.norm === a.toLowerCase()));
        mapping[field.key] = hit ? hit.raw : '';
      }
      columnMapping.value = mapping;
    };

    const handleImportFileSelect = (event) => {
      const file = event.target.files?.[0];
      if (file) parseImportCsvFile(file);
    };

    const handleImportDrop = (event) => {
      importDragging.value = false;
      const file = event.dataTransfer.files?.[0];
      if (file) parseImportCsvFile(file);
    };

    const splitCsvLine = (line) => {
      const result = [];
      let current = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
          if (inQuotes && line[i + 1] === '"') {
            current += '"';
            i++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (ch === ',' && !inQuotes) {
          result.push(current);
          current = '';
        } else {
          current += ch;
        }
      }
      result.push(current);
      return result;
    };

    const normalizeHeader = (header) => String(header || '').trim().toLowerCase().replace(/^\uFEFF/, '');

    const findColumnIndex = (headers, aliases) => {
      for (const alias of aliases) {
        const idx = headers.findIndex((h) => h === alias.toLowerCase());
        if (idx >= 0) return idx;
      }
      return -1;
    };

    const parseImportCsvText = (text, { relaxRequired = false } = {}) => {
      const lines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
      if (lines.length < 1) {
        throw new Error('CSV 至少需要標題列');
      }

      const rawHeaders = splitCsvLine(lines[0]).map((h) => String(h || '').trim().replace(/^\uFEFF/, ''));
      const headers = rawHeaders.map(normalizeHeader);
      if (!relaxRequired) {
        const nameIdx = findColumnIndex(headers, ['name', '姓名']);
        const phoneIdx = findColumnIndex(headers, ['phone', '手機', '手機號碼', 'mobile', '電話']);
        if (nameIdx === -1) throw new Error('找不到姓名欄位（name 或 姓名）');
        if (phoneIdx === -1) throw new Error('找不到手機欄位（phone 或 手機）');
      }

      const columnMap = {};
      Object.entries(FIELD_ALIASES_FOR_PREFILL).forEach(([field, aliases]) => {
        const idx = findColumnIndex(headers, aliases);
        if (idx >= 0) columnMap[field] = idx;
      });

      const items = [];
      const genderValuesSet = new Set();
      for (let i = 1; i < lines.length; i++) {
        const cols = splitCsvLine(lines[i]);
        const row = {};
        Object.entries(columnMap).forEach(([field, idx]) => {
          row[field] = (cols[idx] || '').trim();
        });
        if (!row.name && !row.phone) continue;
        if (row.gender) genderValuesSet.add(row.gender);
        items.push(row);
      }

      const unknownGenderValues = [...genderValuesSet].filter(
        (v) => !BUILTIN_GENDER_MAP[v.toLowerCase()] && !BUILTIN_GENDER_MAP[v]
      );

      return {
        rawHeaders,
        items,
        unknownGenderValues,
        allGenderValues: [...genderValuesSet]
      };
    };

    const applyImportParseResult = (file, parsed, { sampleOnly = false } = {}) => {
      const { rawHeaders, items, allGenderValues } = parsed;
      if (!sampleOnly && items.length === 0) {
        importParseError.value = 'CSV 無有效資料列';
        resetImportState();
        return;
      }
      if (!sampleOnly && items.length > 1000000) {
        importParseError.value = '單次最多匯入 1,000,000 筆';
        resetImportState();
        return;
      }

      importItems.value = items;
      importFileName.value = file.name;
      importFile.value = file;
      importCsvHeaders.value = rawHeaders.filter(Boolean);
      importSampleOnly.value = sampleOnly;
      importSampleRowCount.value = sampleOnly ? items.length : 0;

      if (isWixtarClient.value) {
        prefillColumnMapping(rawHeaders);
      }

      detectedGenderValues.value = allGenderValues;
      const mapping = {};
      allGenderValues.forEach((v) => {
        mapping[v] = BUILTIN_GENDER_MAP[v.toLowerCase()] || BUILTIN_GENDER_MAP[v] || '';
      });
      genderMapping.value = mapping;

      const detectedKeys = new Set();
      for (const item of items) {
        for (const [key, val] of Object.entries(item)) {
          if (!NON_MERGEABLE_FIELDS.has(key) && val !== null && val !== undefined && val !== '') {
            detectedKeys.add(key);
          }
        }
      }
      availableMergeFields.value = [...detectedKeys]
        .filter((k) => FIELD_LABELS[k])
        .sort()
        .map((k) => {
          const example = items.find((r) => r[k])?.[k] || '';
          return { key: k, label: FIELD_LABELS[k].label, example: String(example).slice(0, 12) };
        });
      selectedMergeFields.value = availableMergeFields.value.map((f) => f.key);
    };

    const parseImportCsvFile = (file) => {
      importParseError.value = null;
      const sampleOnly = file.size > IMPORT_SAMPLE_BYTES;

      const onText = (text) => {
        try {
          const cleaned = String(text || '').replace(/^\uFEFF/, '');
          const parsed = parseImportCsvText(cleaned, { relaxRequired: isWixtarClient.value });
          applyImportParseResult(file, parsed, { sampleOnly });
          if (sampleOnly && parsed.rawHeaders.length === 0) {
            importParseError.value = '無法讀取 CSV 表頭';
            resetImportState();
          }
        } catch (e) {
          importParseError.value = e.message || 'CSV 解析失敗';
          resetImportState();
        }
      };

      if (sampleOnly) {
        const blob = file.slice(0, IMPORT_SAMPLE_BYTES);
        const reader = new FileReader();
        reader.onload = () => {
          // 取樣可能切在列中間，丟棄最後不完整列
          let text = String(reader.result || '');
          const lastNl = Math.max(text.lastIndexOf('\n'), text.lastIndexOf('\r'));
          if (lastNl > 0) text = text.slice(0, lastNl);
          onText(text);
        };
        reader.onerror = () => {
          importParseError.value = '讀取檔案失敗';
        };
        reader.readAsText(blob, 'UTF-8');
        return;
      }

      const reader = new FileReader();
      reader.onload = () => onText(reader.result);
      reader.onerror = () => {
        importParseError.value = '讀取檔案失敗';
      };
      reader.readAsText(file, 'UTF-8');
    };

    const escapeHtml = (text) => {
      return String(text || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    };

    // ===================== 進度條 Banner =====================
    const activeImportJob = ref(null);   // { jobNumber, status, progressCurrent, progressTotal, ... }
    let pollTimer = null;

    const IMPORT_JOB_KEY = 'member_import_active_job';

    function saveActiveJob(jobNumber) {
      localStorage.setItem(IMPORT_JOB_KEY, jobNumber);
    }

    function clearActiveJob() {
      localStorage.removeItem(IMPORT_JOB_KEY);
      activeImportJob.value = null;
      if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
    }

    async function pollImportJob(jobNumber) {
      try {
        const res = await memberService.getImportJob(jobNumber);
        if (!res?.success || !res?.data) return;
        const job = res.data;
        activeImportJob.value = job;

        if (['completed', 'partial', 'failed'].includes(job.status)) {
          clearActiveJob();
          // 刷新會員列表
          currentPage.value = 1;
          await fetchMembers();
        }
      } catch (_) {
        // 忽略輪詢錯誤，避免干擾用戶
      }
    }

    function startPolling(jobNumber) {
      saveActiveJob(jobNumber);
      if (pollTimer) clearInterval(pollTimer);
      pollTimer = setInterval(() => pollImportJob(jobNumber), 3000);
      // 立即查一次
      pollImportJob(jobNumber);
    }

    onMounted(() => {
      const savedJob = localStorage.getItem(IMPORT_JOB_KEY);
      if (savedJob) startPolling(savedJob);
      restoreExportBanner();
    });

    onUnmounted(() => {
      if (pollTimer) clearInterval(pollTimer);
      stopExportPolling();
    });

    const importJobProgress = computed(() => {
      const job = activeImportJob.value;
      if (!job) return 0;
      if (!job.progressTotal) return 0;
      return Math.min(100, Math.round((job.progressCurrent / job.progressTotal) * 100));
    });

    const importJobStatusText = computed(() => {
      const job = activeImportJob.value;
      if (!job) return '';
      if (job.status === 'pending') return '排隊中，即將開始...';
      if (job.status === 'processing') return `處理中（${job.progressCurrent || 0} / ${job.progressTotal || job.totalRows || '?'} 筆）`;
      if (job.status === 'completed') return `匯入完成！新增 ${job.createdCount} 筆`;
      if (job.status === 'partial') return `匯入完成，${job.failedCount} 筆失敗`;
      if (job.status === 'failed') return '匯入失敗';
      return '';
    });
    // ===========================================================

    // ===================== 會員匯出 =====================
    const EXPORT_JOB_KEY = 'member_export_active_job';
    const EXPORT_DISMISSED_KEY = 'member_export_dismissed_job';
    const activeExportJob = ref(null);   // { jobNumber, status, progressCurrent, progressTotal, filters, fileExpiresAt, ... }
    const exporting = ref(false);
    const downloadingExport = ref(false);
    let exportPollTimer = null;

    // 與 fetchMembers 相同的篩選條件組裝（不含分頁），供匯出 API 使用
    const buildExportFilters = () => {
      const params = {};
      if (searchQuery.value) params.search = searchQuery.value;
      if (filterStatus.value) params.status = filterStatus.value;
      const filters = advancedFilters.value;
      if (filters.tagIds.length > 0) {
        params.tagIds = filters.tagIds.join(',');
        params.tagMatchType = filters.tagMatchType || 'OR';
      }
      if (filters.registeredStart) params.registeredStart = filters.registeredStart;
      if (filters.registeredEnd) params.registeredEnd = filters.registeredEnd;
      if (filters.birthdayStart) params.birthdayStart = filters.birthdayStart;
      if (filters.birthdayEnd) params.birthdayEnd = filters.birthdayEnd;
      if (filters.birthdayMonth) params.birthdayMonth = Number(filters.birthdayMonth);
      if (filters.birthdayDay) params.birthdayDay = Number(filters.birthdayDay);
      if (filters.addressCity) params.addressCity = filters.addressCity;
      if (filters.addressDistrict) params.addressDistrict = filters.addressDistrict;
      if (filters.addressStreet) params.addressStreet = filters.addressStreet;
      return params;
    };

    function stopExportPolling() {
      if (exportPollTimer) { clearInterval(exportPollTimer); exportPollTimer = null; }
    }

    function dismissExportBanner() {
      const jobNumber = activeExportJob.value?.jobNumber;
      if (jobNumber) localStorage.setItem(EXPORT_DISMISSED_KEY, jobNumber);
      localStorage.removeItem(EXPORT_JOB_KEY);
      activeExportJob.value = null;
      stopExportPolling();
    }

    async function pollExportJob(jobNumber) {
      try {
        const res = await memberService.getExportJob(jobNumber);
        if (!res?.success || !res?.data) return;
        activeExportJob.value = res.data;

        if (['completed', 'failed'].includes(res.data.status)) {
          // 已到終態：停止輪詢、清除追蹤 key；banner 保留供下載或查看結果
          stopExportPolling();
          localStorage.removeItem(EXPORT_JOB_KEY);
        }
      } catch (_) {
        // 忽略輪詢錯誤，避免干擾用戶
      }
    }

    function startExportPolling(jobNumber) {
      localStorage.removeItem(EXPORT_DISMISSED_KEY);
      localStorage.setItem(EXPORT_JOB_KEY, jobNumber);
      stopExportPolling();
      exportPollTimer = setInterval(() => pollExportJob(jobNumber), 3000);
      // 立即查一次
      pollExportJob(jobNumber);
    }

    // 頁面載入時恢復追蹤：優先用 localStorage 的進行中 job，否則顯示最近一次匯出紀錄
    async function restoreExportBanner() {
      const savedJob = localStorage.getItem(EXPORT_JOB_KEY);
      if (savedJob) {
        startExportPolling(savedJob);
        return;
      }
      try {
        const res = await memberService.getExportJobs({ page: 1, pageSize: 1 });
        const latest = res?.data?.items?.[0];
        if (!latest) return;
        if (localStorage.getItem(EXPORT_DISMISSED_KEY) === latest.jobNumber) return;
        activeExportJob.value = latest;
        if (['pending', 'processing'].includes(latest.status)) {
          startExportPolling(latest.jobNumber);
        }
      } catch (_) {
        // 查不到匯出紀錄不影響頁面
      }
    }

    const handleExport = async () => {
      if (exporting.value) return;

      const filters = buildExportFilters();
      const hasFilters = Object.keys(filters).length > 0;

      const bodyHtml = hasFilters
        ? `將依目前套用的篩選條件匯出符合的會員（目前列表共 ${total.value.toLocaleString('zh-TW')} 筆）。`
        : '未套用任何篩選條件，將匯出<strong>全部會員</strong>（含停用、封鎖等所有狀態）。';

      const result = await Swal.fire({
        title: hasFilters ? '匯出會員資料' : '全量匯出會員資料',
        html: `<div style="text-align:left;font-size:14px;line-height:1.7">`
          + `<p style="margin:0">${bodyHtml}</p>`
          + `<p style="margin:8px 0 0;color:#6b7280;font-size:13px">匯出為 CSV 檔（可用 Excel 開啟），檔案保留 7 天。`
          + `其中「最近互動日」自上線月份起計、不含交易與貼標。</p>`
          + `</div>`,
        icon: hasFilters ? 'question' : 'warning',
        showCancelButton: true,
        confirmButtonText: '開始匯出',
        cancelButtonText: '取消'
      });
      if (!result.isConfirmed) return;

      exporting.value = true;
      try {
        const response = await memberService.createExportJob(filters);
        if (response?.success && response.data?.jobNumber) {
          startExportPolling(response.data.jobNumber);
        } else {
          await showError('建立匯出工作失敗', response?.message || '請稍後再試');
        }
      } catch (err) {
        await showError('建立匯出工作失敗', err?.message || '請稍後再試');
      } finally {
        exporting.value = false;
      }
    };

    const handleExportDownload = async () => {
      const job = activeExportJob.value;
      if (!job?.jobNumber || downloadingExport.value) return;

      downloadingExport.value = true;
      try {
        // presigned URL 帶 Content-Disposition: attachment，導航即觸發下載（不受 CORS 限制）
        const response = await memberService.getExportDownloadUrl(job.jobNumber);
        const url = response?.data?.url;
        if (!url) throw new Error('下載連結產生失敗');
        const a = document.createElement('a');
        a.href = url;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (err) {
        await showError('下載失敗', err?.message || '請稍後再試');
      } finally {
        downloadingExport.value = false;
      }
    };

    const exportJobProgress = computed(() => {
      const job = activeExportJob.value;
      if (!job || !job.progressTotal) return 0;
      return Math.min(100, Math.round((job.progressCurrent / job.progressTotal) * 100));
    });

    const exportJobStatusText = computed(() => {
      const job = activeExportJob.value;
      if (!job) return '';
      if (job.status === 'pending') return '匯出排隊中，即將開始...';
      if (job.status === 'processing') return `匯出處理中（${job.progressCurrent || 0} / ${job.progressTotal || job.totalRows || '?'} 筆）`;
      if (job.status === 'completed') return `匯出完成（共 ${(job.progressTotal || job.totalRows || 0).toLocaleString('zh-TW')} 筆）`;
      if (job.status === 'failed') return '匯出失敗';
      return '';
    });

    const exportJobHasFilters = computed(() => {
      const filters = activeExportJob.value?.filters;
      return !!(filters && Object.keys(filters).length > 0);
    });

    const exportFileExpired = computed(() => {
      const expiresAt = activeExportJob.value?.fileExpiresAt;
      if (!expiresAt) return false;
      return new Date(expiresAt).getTime() < Date.now();
    });
    // ===========================================================

    const handleImportSubmit = async () => {
      if (!canSubmitImport.value || importing.value || !importFile.value) return;

      if (isWixtarClient.value && !columnMapping.value.phone) {
        importParseError.value = '請對應手機欄位（phone）';
        return;
      }

      importing.value = true;
      try {
        // 1. 取得預簽名 PUT URL（key 綁定租戶、僅限 CSV content-type、短效期）
        const file = importFile.value;
        const fileType = file.type || 'text/csv';
        const urlRes = await memberService.getImportUploadUrl({
          fileName: file.name,
          fileType,
          fileSize: file.size
        });
        const { presignedUrl, objectKey } = urlRes?.data || {};
        if (!presignedUrl || !objectKey) {
          throw new Error(urlRes?.message || '無法取得上傳 URL');
        }

        // 2. 原始 CSV 整檔直傳 S3（繞過 API Gateway 10MB；後端串流分段處理可達百萬列）
        const putRes = await fetch(presignedUrl, {
          method: 'PUT',
          headers: { 'Content-Type': fileType },
          body: file
        });
        if (!putRes.ok) {
          throw new Error(`檔案上傳失敗 (${putRes.status})，請稍後再試`);
        }

        // 3. 送 S3 key 與匯入選項建立 job；大檔筆數由後端 prepare 掃描覆寫
        const mappingPayload = isWixtarClient.value
          ? Object.fromEntries(
            Object.entries(columnMapping.value).filter(([, v]) => v !== null && v !== undefined && v !== '')
          )
          : null;

        const response = await memberService.importMembersFromS3(objectKey, {
          duplicateStrategy: importDuplicateStrategy.value,
          fileName: importFileName.value || null,
          mergeFields: importDuplicateStrategy.value === 'merge'
            ? (selectedMergeFields.value.length > 0 ? selectedMergeFields.value : null)
            : undefined,
          genderMap: Object.keys(genderMapping.value).length > 0 ? genderMapping.value : null,
          columnMapping: mappingPayload,
          syncToWixtar: false,
          totalRows: importSampleOnly.value ? 0 : importItems.value.length
        });
        if (response?.success && response.data) {
          const { jobNumber } = response.data;
          closeImportModal();
          // 開始輪詢進度
          startPolling(jobNumber);
        } else {
          await showError('建立匯入工作失敗', response?.message || '請稍後再試');
        }
      } catch (err) {
        const msg = err?.response?.data?.message || err?.message || '匯入失敗，請稍後再試';
        importParseError.value = msg;
      } finally {
        importing.value = false;
      }
    };

    const handleView = (member) => {
      router.push(`/client/members/${member.id}`);
    };

    const handleEdit = (member) => {
      router.push(`/client/members/${member.id}/edit`);
    };

    const handleDelete = async (member) => {
      const memberName = member.name || member.phone || '此會員';
      
      // 列出會一起刪除的關聯資料
      const relatedData = [
        '點數交易記錄',
        '優惠券發放記錄',
        '優惠券轉讓記錄',
        '批次優惠券發放明細',
        '預約記錄',
        '取貨預約',
        'LINE 訊息記錄',
        'LINE 點擊記錄',
        'LINE 封鎖記錄',
        '分析記錄（CLV、RFM）',
        '自動發送記錄',
        '客服工單',
        'LIFF 會話記錄',
        'Chatmate 對話記錄',
        '候補名單記錄'
      ];
      
      // 使用 showDeleteConfirm 並傳入關聯資料列表
      const confirmed = await showDeleteConfirm(memberName, relatedData);
      
      if (!confirmed) {
        return;
      }

      const originalLoading = loading.value;
      loading.value = true;
      error.value = null;

      try {
        const response = await memberService.deleteMember(member.id);

        if (response && response.success) {
          await showSuccess('刪除成功', `會員「${member.name || member.phone}」已成功刪除`);
          
          if (members.value.length === 1 && currentPage.value > 1) {
            currentPage.value = currentPage.value - 1;
          }
          
          await fetchMembers();
        } else {
          const errorMsg = (response && response.message) || '刪除會員失敗';
          await showError('刪除失敗', errorMsg);
        }
      } catch (err) {
        console.error('刪除會員失敗:', err);
        
        let errorTitle = '刪除失敗';
        let errorText = '刪除會員失敗，請稍後再試';
        
        if (err.code === 'NOT_FOUND') {
          errorText = '會員不存在或已被刪除';
        } else if (err.code === 'FORBIDDEN') {
          errorText = '您沒有權限刪除此會員';
        } else if (err.message) {
          errorText = err.message;
        }
        
        await showError(errorTitle, errorText);
      } finally {
        loading.value = originalLoading;
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    };

    const formatCurrency = (amount) => {
      if (amount === null || amount === undefined) return 'NT$ 0';
      return `NT$ ${Number(amount).toLocaleString('zh-TW')}`;
    };

    const formatNumber = (number) => {
      if (number === null || number === undefined) return '0';
      return Number(number).toLocaleString('zh-TW');
    };

    const getStatusBadgeClass = (status) => {
      const statusMap = {
        active: 'badge badge-success',
        inactive: 'badge badge-danger',
        pending: 'badge badge-warning',
        blocked: 'badge badge-danger',
      };
      return statusMap[status] || 'badge badge-secondary';
    };

    const getStatusText = (status) => {
      const statusMap = {
        active: '啟用',
        inactive: '停用',
        pending: '待審核',
        blocked: '已封鎖',
      };
      return statusMap[status] || status;
    };

    onMounted(() => {
      loadClientMemberSystem();
      fetchMembers();
      loadCities();
    });

    return {
      members,
      loading,
      error,
      searchQuery,
      filterStatus,
      isWixtarClient,
      showAdvancedSearch,
      advancedFilters,
      monthOptions,
      dayOptions,
      cities,
      districts,
      selectedCityId,
      loadingCities,
      loadingDistricts,
      activeImportJob,
      importJobProgress,
      importJobStatusText,
      clearActiveJob,
      activeExportJob,
      exporting,
      downloadingExport,
      exportJobProgress,
      exportJobStatusText,
      exportJobHasFilters,
      exportFileExpired,
      handleExport,
      handleExportDownload,
      dismissExportBanner,
      showImportModal,
      importDragging,
      importFileName,
      importFile,
      importItems,
      importParseError,
      importing,
      importDuplicateStrategy,
      importFileInput,
      importCsvHeaders,
      importSampleOnly,
      importSampleRowCount,
      columnMapping,
      importMappingFields,
      canSubmitImport,
      importSubmitLabel,
      genderMapping,
      detectedGenderValues,
      availableMergeFields,
      selectedMergeFields,
      selectAllMergeFields,
      clearAllMergeFields,
      handleCityChange,
      activeAdvancedFilterCount,
      currentPage,
      totalPages,
      total,
      pageTitle,
      handleSearch,
      handleFilterChange,
      toggleAdvancedSearch,
      applyAdvancedFilters,
      resetAdvancedFilters,
      goToPage,
      handleCreate,
      openImportModal,
      closeImportModal,
      downloadImportTemplate,
      triggerImportFileSelect,
      clearImportFile,
      handleImportFileSelect,
      handleImportDrop,
      handleImportSubmit,
      handleView,
      handleEdit,
      handleDelete,
      formatDate,
      formatCurrency,
      formatNumber,
      getStatusBadgeClass,
      getStatusText,
      fetchMembers,
    };
  },
};
</script>

<style scoped>
@import '~@/assets/scss/components/_table-common.scss';
@import '~@/assets/scss/components/_list-page-common.scss';

.table-wrapper table {
  min-width: 1200px;
}

/* Wixtar 客戶提示（配色沿用本頁匯入進度 Banner） */
.wixtar-banner {
  margin: 0 0 16px;
  padding: 12px 16px;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  background: #f0f7ff;
  color: #1e293b;
  font-size: 14px;
  line-height: 1.7;

  a {
    color: #2563eb;
    text-decoration: underline;
  }
}

.th-hint {
  font-size: 12px;
  font-weight: 400;
  color: #6b7280;
  white-space: nowrap;
}

/* 匯入進度條 Banner */
.import-progress-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  margin-bottom: 16px;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  background: #f0f7ff;
  font-size: 14px;

  &.banner-completed { background: #f0fdf4; }
  &.banner-partial   { background: #fffbeb; }
  &.banner-failed    { background: #fef2f2; }

  .banner-icon {
    font-size: 20px;
    flex-shrink: 0;
    color: #2563eb;
    .banner-completed & { color: #16a34a; }
    .banner-partial &   { color: #ca8a04; }
    .banner-failed &    { color: #dc2626; }

    .spinning {
      display: inline-block;
      animation: spin 1s linear infinite;
    }
  }

  .banner-content {
    flex: 1;
    min-width: 0;
  }

  .banner-text {
    font-weight: 500;
    color: #1e293b;
    margin-bottom: 4px;
  }

  .banner-bar-wrap {
    height: 6px;
    background: #dbeafe;
    border-radius: 3px;
    overflow: hidden;
    margin-bottom: 4px;
  }

  .banner-bar {
    height: 100%;
    background: #2563eb;
    border-radius: 3px;
    transition: width 0.4s ease;
    min-width: 4px;
  }

  .banner-sub {
    font-size: 12px;
    color: #6b7280;
  }

  .banner-actions {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
  }

  .btn-icon {
    width: 28px;
    height: 28px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: #6b7280;
    background: transparent;
    border: none;
    cursor: pointer;
    &:hover { background: rgba(0,0,0,0.08); }
  }

  .btn-link {
    font-size: 13px;
    color: #2563eb;
    background: none;
    border: none;
    padding: 4px 8px;
    cursor: pointer;
    text-decoration: underline;
    &:hover { color: #1d4ed8; }
  }

  .banner-expired {
    font-size: 13px;
    color: #9ca3af;
    white-space: nowrap;
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

.advanced-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.filter-count {
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: #667eea;
  color: #fff;
  font-size: 12px;
  line-height: 20px;
  text-align: center;
}

.advanced-search-panel {
  margin-bottom: 20px;
  padding: 20px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.advanced-section {
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #f0f0f0;
}

.advanced-section:last-of-type {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.advanced-section-title {
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 14px;
  color: #111827;
}

.advanced-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
}

.compact-grid {
  margin-top: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group label {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.full-width {
  grid-column: 1 / -1;
}

.advanced-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
}

@media (max-width: 768px) {
  .advanced-search-panel {
    padding: 16px;
  }

  .advanced-grid {
    grid-template-columns: 1fr;
  }

  .advanced-actions {
    justify-content: stretch;
  }

  .advanced-actions .btn {
    flex: 1;
  }
}

.header-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 16px;
}

.modal-container {
  width: 100%;
  max-width: 640px;
  max-height: 90vh;
  overflow: auto;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.modal-close {
  border: none;
  background: transparent;
  font-size: 22px;
  color: #6b7280;
  cursor: pointer;
}

.modal-body {
  padding: 20px 24px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px 24px;
  border-top: 1px solid #e5e7eb;
}

.import-hint-wixtar {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 10px 12px;
}

.import-large-file-hint {
  margin: 8px 0 0;
  font-size: 13px;
  color: #1d4ed8;
  display: flex;
  align-items: flex-start;
  gap: 6px;
}

.column-mapping-section {
  margin-top: 16px;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fafafa;
}

.column-mapping-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  margin-bottom: 10px;
  color: #111827;

  .column-mapping-hint {
    font-weight: 400;
    font-size: 12px;
    color: #6b7280;
  }
}

.column-mapping-rows {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px 12px;
}

.column-mapping-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.column-mapping-label {
  font-size: 13px;
  font-weight: 500;
  color: #374151;

  .required-mark {
    color: #dc2626;
    margin-left: 2px;
  }
}

.import-hint {
  margin: 0 0 16px;
  font-size: 14px;
  color: #4b5563;
  line-height: 1.6;
}

.import-template-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.import-template-label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.import-sample {
  margin-bottom: 16px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
  font-size: 13px;
}

.import-sample pre {
  margin: 8px 0 0;
  white-space: pre-wrap;
  word-break: break-all;
  font-size: 12px;
}

.import-strategy {
  margin-bottom: 12px;
}

/* 合併欄位勾選區 */
.merge-fields-section {
  margin: 0 0 16px;
  padding: 14px 16px;
  background: #f8faff;
  border: 1px solid #dbeafe;
  border-radius: 8px;
}

.merge-fields-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.merge-fields-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
  font-size: 13px;
  color: #1e40af;
  flex: 1;

  i { font-size: 15px; }
}

.merge-fields-bulk {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;

  .divider { color: #d1d5db; }
}

.btn-text {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #2563eb;
  font-size: 12px;
  text-decoration: underline;
  &:hover { color: #1d4ed8; }
}

.merge-fields-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 8px;
  margin-bottom: 10px;
}

.merge-field-item {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  padding: 7px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: #93c5fd;
  }

  &.is-checked {
    border-color: #3b82f6;
    background: #eff6ff;
  }

  input[type="checkbox"] {
    flex-shrink: 0;
    margin-top: 1px;
    accent-color: #2563eb;
  }

  .field-label {
    font-size: 13px;
    font-weight: 500;
    color: #374151;
    line-height: 1.3;
  }

  .field-example {
    font-size: 11px;
    color: #9ca3af;
    display: block;
    margin-top: 1px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  }
}

.merge-fields-hint {
  font-size: 12px;
  color: #6b7280;
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: 4px;

  i { flex-shrink: 0; margin-top: 1px; }
}

.import-dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 140px;
  padding: 24px;
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  background: #fafafa;
  text-align: center;
}

.import-dropzone.is-dragover {
  border-color: #3b82f6;
  background: #eff6ff;
}

.import-dropzone i {
  font-size: 28px;
  color: #3b82f6;
}

.import-error {
  margin: 12px 0 0;
  color: #dc2626;
  font-size: 14px;
}
</style>
